import type {TagColor} from "~/components/tag/tag.styles";
import type {
    BuComplianceStatus,
    ComplianceArtifactKind,
    ComplianceLayer,
    ComplianceRequirementState,
    QualificationStatus,
} from "~/pages/supplier-360/types/Supplier360Types";

/**
 * Shared by the records and business-unit columns — the API's lowercase values in the design's copy.
 * Record `state` chips live in `~/components/status-chip/statusChips` (`STATUS_CHIP`); reminder
 * chips in `complianceReminderChips` (`REMINDER_CHIP`).
 */

export const LAYER_LABEL: Record<ComplianceLayer, string> = {
    company_wide: 'Company-wide',
    business_unit: 'Business unit',
};

export const QUALIFICATION_LABEL: Record<QualificationStatus, string> = {
    approved: 'Approved',
    conditional: 'Conditional',
    suspended: 'Suspended',
};

export const QUALIFICATION_COLOR: Record<QualificationStatus, TagColor> = {
    approved: 'success',
    conditional: 'warning',
    suspended: 'error',
};

/**
 * A company-wide record's Qualification chip on its detail page — derived from the record `state`
 * (`SxcRequirementDetail`): Met → Accepted, Pending → Under review, otherwise Not accepted.
 * Expiring still counts as Accepted: the evidence is valid until it lapses.
 */
export const COMPANY_QUALIFICATION_LABEL: Record<ComplianceRequirementState, string> = {
    met: 'Accepted',
    expiring: 'Accepted',
    pending: 'Under review',
    expired: 'Not accepted',
    missing: 'Not accepted',
};

export const COMPANY_QUALIFICATION_COLOR: Record<ComplianceRequirementState, TagColor> = {
    met: 'success',
    expiring: 'success',
    pending: 'warning',
    expired: 'error',
    missing: 'error',
};

/** `bu_qualification.status` (§2.7) — the Business units table's "Compliance status" column. */
export const BU_STATUS_LABEL: Record<BuComplianceStatus, string> = {
    compliant: 'Compliant',
    expiring_soon: 'Expiring soon',
    non_compliant: 'Non-compliant',
    pending_review: 'Pending review',
};

/** Evidence `kind` (§2.9) — the backend's own `Kind` labels. */
export const ARTIFACT_KIND_LABEL: Record<ComplianceArtifactKind, string> = {
    certificate: 'Certificate',
    declaration: 'Declaration',
    insurance: 'Insurance',
    permit: 'Permit',
    audit_report: 'Audit report',
    questionnaire: 'Questionnaire',
    test_result: 'Test result',
    contract_annex: 'Contract annex',
    other: 'Other evidence',
};
